import { Sparkles } from "lucide-react";
import { useEffect, useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaComboboxField } from "@/shared/components/forms/ColocaComboboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useOrganizationDirectory } from "@/shared/hooks/useOrganizationDirectory";
import useTranslations from "@/shared/lang/useTranslations";

import { USER_LANGUAGE_OPTIONS, USER_LANGUAGES, type UserLanguage } from "../constants/users.constants";
import { CREATE_FIELDS } from "../hooks/useUserMutations";
import type { CreateUserPayload } from "../types/create-user.types";
import { UserStatus } from "../types/create-user.types";
import { isEmailConflict, isProbableDeletedEmailReuse, type NormalizedApiError } from "../utils/apiError";
import { generateSecurePassword, STRONG_PASSWORD_PATTERN } from "../utils/password.utils";
import { PHONE_PATTERN } from "../utils/userProfileForm";

function buildSchema(messages: {
  required: string;
  email: string;
  phone: string;
  password: string;
  mismatch: string;
}) {
  return z
    .object({
      full_name: z.string().trim().min(2, messages.required).max(255),
      email: z.string().trim().min(1, messages.required).email(messages.email).max(255),
      organization_id: z.string().min(1, messages.required),
      preferred_language: z.enum(USER_LANGUAGES),
      country: z.string().max(100),
      // Same rule the edit drawer enforces, from the same constant. Without it the hint below the field was
      // advisory only: typing `+52 55 1234 5678` — the exact mistake it warns about — passed the client and
      // came back as a raw Spanish `class-validator` sentence from the API.
      phone_number: z
        .string()
        .trim()
        .refine((value) => value === "" || PHONE_PATTERN.test(value), messages.phone),
      // One `.regex()` with OUR message. A chain of un-messaged rules surfaces Zod's English defaults —
      // including the pattern source — straight into the UI, which is both wrong and untranslated.
      password: z.string().min(1, messages.required).regex(STRONG_PASSWORD_PATTERN, messages.password),
      password_confirm: z.string(),
    })
    .refine((values) => values.password === values.password_confirm, {
      path: ["password_confirm"],
      message: messages.mismatch,
    });
}

/**
 * Is this key one of the form's own inputs?
 *
 * `normalizeUserApiError` is already given `CREATE_FIELDS` and binds nothing else, but `fieldErrors` is typed
 * `Record<string, string>`, so the guarantee is invisible at the call site. Narrowing with the same tuple
 * states the real union of field names instead of asserting one member of it — and because the guard's output
 * has to satisfy `setError`, a name that stops being a form field fails to compile rather than binding an
 * error to an input that does not exist.
 */
function isCreateField(field: string): field is (typeof CREATE_FIELDS)[number] {
  return (CREATE_FIELDS as readonly string[]).includes(field);
}

export interface CreateUserDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isBusy: boolean;
  error: NormalizedApiError | null;
  onSubmit: (payload: CreateUserPayload) => void;
}

const CREATE_FORM_ID = "create-user-form";

/**
 * Creates an ORGANIZATION MEMBER. That is not a product simplification — the API cannot persist an INTERNAL
 * user: the service refuses `organization_id` for `user_type: internal`, the column is NOT NULL, and no code
 * resolves the root organization at runtime. So `user_type` is not a control, and the drawer says why.
 *
 * Three payload values are non-negotiable and none of them is a form field:
 *
 * - **`status: "active"`** — the entity default `pending_verification` blocks 2FA enrolment (`enable2FA`
 *   requires ACTIVE), so a person created with it could never enrol a device.
 * - **`two_factor_enabled: false`** — the entity default is `true` with a null secret, which is a permanent
 *   login lockout: login demands a code and `enable2FA` refuses with `ALREADY_ENABLED`.
 * - **Empty optional strings are OMITTED, never sent as `""`.** `@IsOptional()` does not skip an empty string,
 *   so `phone_number: ""` reaches the phone validator and 400s. This one rule removes the most common failure
 *   of the screen this replaces.
 */
export function CreateUserDrawer({ open, onOpenChange, isBusy, error, onSubmit }: CreateUserDrawerProps) {
  const { t, activeLanguage } = useTranslations("users");
  // Gated on `open`, not hardcoded `true`. The drawer is mounted for the whole page lifetime (only `open`
  // toggles), so an unconditional `true` fired the catalog request on every visit — including for a
  // non-INTERNAL viewer, for whom `GET /internal/organizations` is a guaranteed 403 that then latches
  // `preloadDenied` module-wide for the rest of the session.
  const directory = useOrganizationDirectory(open);

  const schema = useMemo(
    () =>
      buildSchema({
        required: t("validation_required"),
        email: t("validation_email"),
        phone: t("create_field_phone_hint"),
        password: t("password_requirements"),
        mismatch: t("password_mismatch"),
      }),
    [t],
  );

  const defaultLanguage: UserLanguage = (USER_LANGUAGES as readonly string[]).includes(activeLanguage)
    ? (activeLanguage as UserLanguage)
    : "es";

  const form = useZodForm(schema, {
    full_name: "",
    email: "",
    organization_id: "",
    preferred_language: defaultLanguage,
    country: "",
    phone_number: "",
    password: "",
    password_confirm: "",
  });

  /**
   * Reset on BOTH edges of `open`, and declared BEFORE the binding effect so a message that belongs to this
   * attempt survives it.
   *
   * The close edge drops what was typed. The open edge is the one that matters: `error` is a page-level slot
   * shared with the detail drawer, and this component stays mounted for the whole page lifetime — so a failed
   * EDIT used to paint "this email already exists" onto this form while it was closed, and nothing cleared
   * it. The operator then opened Crear usuario to a red error under an empty field.
   */
  useEffect(() => {
    form.reset();
  }, [open, form]);

  // Bind server-side messages to the inputs that own them, so a duplicate email lands on the email field
  // rather than in a toast the operator has to translate into an action. Gated on `open` for the same reason
  // as the reset above — a closed drawer must not react to another drawer's failure.
  useEffect(() => {
    if (!open || !error) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      if (!isCreateField(field)) continue;
      form.setError(field, {
        type: "server",
        // The API's own 409 sentence is English and quotes the address back — rule 6 says this is translated.
        message: field === "email" && isEmailConflict(error) ? t("error_email_taken") : message,
      });
    }
    // Recreating a soft-deleted user's email does NOT answer 409: the service pre-checks with a
    // soft-delete-scoped `findOne`, so the address passes and the insert then violates `UNIQUE (email)` —
    // a constraint with no `deleted_at` predicate. That escapes the i18n filter as a bare 500. Offer it as a
    // possible cause with a next step, rather than leaving "internal server error" as the only explanation —
    // the copy is conditional because a 500 carries no code and a database outage looks identical.
    if (isProbableDeletedEmailReuse(error) && Object.keys(error.fieldErrors).length === 0) {
      form.setError("email", { type: "server", message: t("error_email_deleted_reuse") });
    }
  }, [open, error, form, t]);

  const organizationItems = useMemo(
    () => directory.organizations.map((org) => ({ value: org.id, label: org.name })),
    [directory.organizations],
  );

  /**
   * What the dropdown says when it has nothing to show.
   *
   * `ColocaComboboxField` defaults to the hardcoded English `"No results."`, and the preload starts on the
   * rising edge of `open` and walks up to five pages — so on a cold session the operator opened this field to
   * "No results." and concluded there were no organizations. Same message if the catalog 403'd or failed.
   * `organization_id` is required, so this is the one field that can block the whole flow.
   */
  const organizationEmptyMessage = directory.isPreloading
    ? t("org_directory_loading")
    : directory.hasError || directory.accessDenied
      ? t("org_directory_failed")
      : t("org_directory_empty");

  const submit = (values: {
    full_name: string;
    email: string;
    organization_id: string;
    preferred_language: UserLanguage;
    country: string;
    phone_number: string;
    password: string;
  }) => {
    const payload: CreateUserPayload = {
      full_name: values.full_name.trim(),
      email: values.email.trim().toLowerCase(),
      password: values.password,
      organization_id: values.organization_id,
      status: UserStatus.ACTIVE,
      two_factor_enabled: false,
    };
    // Omit rather than send an empty string — see the doc block above.
    const country = values.country.trim();
    if (country) payload.country = country;
    const phone = values.phone_number.trim();
    if (phone) payload.phone_number = phone;
    if (values.preferred_language) payload.preferred_language = values.preferred_language;

    onSubmit(payload);
  };

  const generate = () => {
    const next = generateSecurePassword(16);
    form.setValue("password", next, { shouldValidate: true });
    form.setValue("password_confirm", next, { shouldValidate: true });
  };

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      // The body is a single root element, so the drawer would otherwise pin its LAST child to the
      // bottom edge — here the credentials block, which is content, not an action row. That pin is what
      // dragged it down a tall panel (mt-auto) and floated it over the scrolling fields on a short one
      // (sticky bottom-0). The actions live in the footer prop, so nothing needs pinning.
      pinLastChild={false}
      title={t("create_title")}
      description={t("create_subtitle")}
      footer={
        <div className="flex w-full flex-col gap-3">
          {/* One line only. The "no email is sent" warning moved next to the password itself, where it is
              actionable rather than a third line of small print nobody reads. */}
          <p className="text-xs text-muted-foreground">{t("create_note_must_change")}</p>
          <div className="flex items-center justify-end gap-2">
            <ColocaButton variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              {t("detail_close")}
            </ColocaButton>
            {/* `type="submit" form=…` wires the footer button to the form it sits outside, which is also
                what makes Enter inside any field submit. A bare onClick left Enter doing nothing at all. */}
            <ColocaButton type="submit" form={CREATE_FORM_ID} variant="primary" size="sm" loading={isBusy}>
              {t("create_submit")}
            </ColocaButton>
          </div>
        </div>
      }
    >
      <ColocaForm id={CREATE_FORM_ID} form={form} onSubmit={submit} className="space-y-4">
        {error && Object.keys(error.fieldErrors).length === 0 && error.message && (
          <ColocaAlert variant="destructive" text={error.message} />
        )}

        <ColocaInputField name="full_name" label={t("create_field_full_name")} autoComplete="off" />
        <ColocaInputField name="email" type="email" label={t("create_field_email")} autoComplete="off" />
        <ColocaComboboxField
          name="organization_id"
          label={t("create_field_organization")}
          placeholder={t("create_field_organization_placeholder")}
          items={organizationItems}
          emptyMessage={organizationEmptyMessage}
          description={directory.isTruncated ? t("org_directory_truncated") : undefined}
        />

        {/* Same `items-start` reason as the credentials grid below: an error on one column must not
            redistribute the other column's rows. */}
        <div className="grid items-start gap-4 sm:grid-cols-2">
          <ColocaSelectField
            name="preferred_language"
            label={t("create_field_language")}
            items={[...USER_LANGUAGE_OPTIONS]}
          />
          <ColocaInputField name="country" label={t("create_field_country")} maxLength={100} />
        </div>

        <ColocaInputField
          name="phone_number"
          label={t("create_field_phone")}
          description={t("create_field_phone_hint")}
          inputMode="tel"
        />

        {/*
         * Credentials are their own concern, so they read as their own block rather than as two more rows
         * of the profile form.
         *
         * "Generar" lives in the section header, not glued to the password input, because it fills BOTH
         * fields — an action on the group, not on one control. Gluing it to the first input also forced a
         * stretched `items-end` flex whose in-cell helper text was three lines tall, which pushed the row
         * open and dragged the "Confirmar" label out of alignment with "Contraseña".
         *
         * The rule sits once under the header instead of inside a grid cell, so neither column is taller
         * than the other and the two fields line up exactly.
         */}
        <ColocaSurface variant="muted" padding="md" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-foreground">{t("create_section_credentials")}</span>
            <ColocaButton type="button" variant="secondary" size="sm" icon={Sparkles} onClick={generate}>
              {t("create_generate")}
            </ColocaButton>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-xs text-muted-foreground">{t("password_requirements")}</p>
            {/* Operationally load-bearing: `UsersService` injects no mail service, so nothing is sent. An
                operator who assumes otherwise creates the person and leaves them stranded. */}
            <p className="text-xs text-warning">{t("create_note_no_email")}</p>
          </div>

          {/* `items-start` so a validation message under one field can never stretch the other cell: a
              stretched `FormItem` is a grid of auto rows, and the free space spreads between its label and
              its input. */}
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <ColocaInputField name="password" label={t("create_field_password")} autoComplete="off" />
            <ColocaInputField name="password_confirm" label={t("password_confirm")} autoComplete="off" />
          </div>
        </ColocaSurface>
      </ColocaForm>
    </ColocaDrawer>
  );
}
